import { gql, useQuery } from '@apollo/client';
import React, { useCallback } from 'react';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import { NoData } from 'src/components/common/noData/NoData';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { TimeRangeOption } from 'src/lib/config/timeRangeOptions';
import { date } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { SelectedSubgraph } from '../../../operationsPage/insightsL3Nav/insightsList/SubgraphsList';
import { insightsRouteConfig } from '../../../routes';

import {
  FetchErrorsOverTime,
  fetchErrorsOverTimeResultFragment,
} from './FetchErrorsOverTime';
import {
  FetchesOverTime,
  fetchesOverTimeResultFragment,
} from './FetchesOverTime';
import {
  FetchLatencyOverTime,
  fetchLatencyOverTimeResultFragment,
} from './FetchLatencyOverTime';

export const subgraphServiceReportQuery = gql`
  query SubgraphServiceReportQuery(
    $graphId: ID!
    $variant: String!
    $resolution: Resolution!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $subgraphName: String!
  ) {
    graph(id: $graphId) {
      id
      variant(name: $variant) {
        id
        subgraphInsightsTimeseries(
          from: $timeFrom
          to: $timeTo
          resolution: $resolution
          requireLatencyMetrics: true
          filter: { subgraphName: $subgraphName }
        ) {
          records {
            timestamp
          }
          ...FetchesOverTimeResultFragment
          ...FetchErrorsOverTimeResultFragment
          ...FetchLatencyOverTimeResultFragment
        }
      }
    }
  }
  ${fetchesOverTimeResultFragment}
  ${fetchErrorsOverTimeResultFragment}
  ${fetchLatencyOverTimeResultFragment}
`;

interface Props {
  graphId: string;
  graphVariant: string;
  selectedSubgraph: SelectedSubgraph;
  selectedRange: TimeRangeOption;
}

export const SubgraphPageCharts = ({
  graphId,
  graphVariant,
  selectedSubgraph,
  selectedRange,
}: Props) => {
  const isInBackground = useIsAppInBackgroundContext();
  const { from, to, fastModeRange } = useRouteParams(insightsRouteConfig);

  const { timeFrom, timeTo, resolution, pollInterval } = date.getDateBounds(
    selectedRange,
    { from, to },
    undefined,
    fastModeRange,
  );

  const { data, loading } = useQuery<
    GraphQLTypes.SubgraphServiceReportQuery,
    GraphQLTypes.SubgraphServiceReportQueryVariables
  >(subgraphServiceReportQuery, {
    notifyOnNetworkStatusChange: false,
    pollInterval,
    skip: isInBackground || !graphVariant || !selectedSubgraph,
    skipPollAttempt: useCallback(() => isInBackground, [isInBackground]),
    variables: {
      graphId,
      variant: graphVariant,
      resolution,
      timeFrom,
      timeTo,
      subgraphName: selectedSubgraph?.name,
    },
  });

  const timeseriesResult = data?.graph?.variant?.subgraphInsightsTimeseries;
  const hasFetchData = (timeseriesResult?.records.length ?? 0) > 0;

  if (!loading && !hasFetchData) {
    return (
      <NoData
        message="No data"
        secondary="Try adjusting time range or selecting a different subgraph."
      />
    );
  }

  return (
    <CursorProvider>
      <FetchesOverTime
        loading={loading}
        result={timeseriesResult}
        resolution={resolution}
      />
      <FetchErrorsOverTime
        className="mt-4"
        loading={loading}
        result={timeseriesResult}
        resolution={resolution}
      />
      <FetchLatencyOverTime
        className="mt-4"
        loading={loading}
        result={timeseriesResult}
        resolution={resolution}
      />
    </CursorProvider>
  );
};
